import { addDays } from 'date-fns';

import {
    BasicsForm,
    CategoriesForm,
    ConditionsForm,
    ContactForm,
    MatchingWizardForm,
    PrinciplesForm,
    WeightingForm
} from '@dh/screens/matching/types';
import {
    Institution,
    REditMatchingBasics,
    REditMatchingConstraints,
    REditMatchingPrinciples,
    REditMatchingScoredTopics,
    REditMatchingTopics,
    RMatchingBody,
    StrapiDocument,
    User
} from '@dh/types';
import { makeStrapiRelation, makeStrapiRelations } from '@dh/utils';

import { ProlongDays } from '../../../../constants';

const mapBasicsFormToBody = (form?: BasicsForm): REditMatchingBasics | {} => {
    if (!form) return {};
    // null is not allowed for title and description
    return {
        title: form.title.value ?? '',
        description: form.description.value ?? ''
    };
};

const mapTopicsFormToBody = (form?: CategoriesForm): REditMatchingTopics | {} => {
    if (!form) return {};
    return {
        topics: makeStrapiRelations(form.topics.value)
    };
};

const mapScoredTopicsFormToBody = (form?: WeightingForm): REditMatchingScoredTopics | {} => {
    if (!form) return {};
    return {
        scored_topics: makeStrapiRelations(form.topics.value?.filter((tp) => tp.isDoubledScored))
    };
};

const mapPrinciplesFormToBody = (form?: PrinciplesForm): REditMatchingPrinciples | {} => {
    if (!form) return {};
    return {
        principles: makeStrapiRelations(form.principles.value)
    };
};

const mapConditionsFormToBody = (form?: ConditionsForm): REditMatchingConstraints | {} => {
    if (!form) return {};
    return {
        constraints: {
            languages: makeStrapiRelations(form.languages.value),

            date_of_finalization:
                form.has_date_of_finalization.value && !!form.date_of_finalization.value
                    ? form.date_of_finalization.value?.toISOString()
                    : null,
            geographic_radius:
                form.has_geographic_radius.value && !!form.geographic_radius.value
                    ? form.geographic_radius.value
                    : null,
            geographic_address:
                form.has_geographic_radius.value && !!form.geographic_address.value
                    ? form.geographic_address.value.fullAddress
                    : null,
            geographic_address_longitude:
                form.has_geographic_radius.value && !!form.geographic_address.value
                    ? form.geographic_address.value.coordinates[0]
                    : null,
            geographic_address_latitude:
                form.has_geographic_radius.value && !!form.geographic_address.value
                    ? form.geographic_address.value.coordinates[1]
                    : null,
            budget:
                form.has_budget.value && form.budget.value?.min !== undefined
                    ? {
                          min: form.budget.value.min,
                          max:
                              form.budget.value.max === 'NaN' || !form.budget.value.max
                                  ? null
                                  : form.budget.value.max
                      }
                    : null
        }
    };
};

const mapContactFormToBody = ({
    form,
    defaultContact
}: {
    form?: ContactForm;
    defaultContact: User;
}): RMatchingBody => {
    if (!form) return {};
    // the contact is mandatory
    const contact = form.contact.value ?? defaultContact;
    return {
        contact: makeStrapiRelation(contact)
    };
};

const mapMiscellaneousFormToBody = ({ owner }: { owner: StrapiDocument }): RMatchingBody => {
    const endAt = addDays(new Date(), ProlongDays);
    return {
        endAt: endAt.toISOString(),
        owner: makeStrapiRelation(owner)
    };
};

export const mapMatchingFormToBody = ({
    form,
    defaultOwner,
    defaultContact
}: {
    form: Partial<MatchingWizardForm>;
    defaultOwner: Institution;
    defaultContact: User;
}): RMatchingBody => {
    return {
        ...mapBasicsFormToBody(form.basics),
        ...mapTopicsFormToBody(form.categories),
        ...mapScoredTopicsFormToBody(form.weighting),
        ...mapPrinciplesFormToBody(form.principles),
        ...mapConditionsFormToBody(form.conditions),
        ...mapContactFormToBody({ form: form.contact, defaultContact: defaultContact }),
        ...mapMiscellaneousFormToBody({ owner: defaultOwner })
    };
};
